import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Companies, Company } from 'app/core/company/company.types';
import { BehaviorSubject, map, Observable, ReplaySubject, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CompanyService {
    private _companies: ReplaySubject<Companies> = new ReplaySubject<Companies>(1);
    private _company: BehaviorSubject<Company | null> = new BehaviorSubject<Company | null>(null);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for company
     *
     * @param value
     */
    set company(value: Company | null) {
        // Store the value
        this._company.next(value);
    }

    get company$(): Observable<Company | null> {
        return this._company.asObservable();
    }

    get companies$(): Observable<Companies> {
        return this._companies.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get the current logged in company data
     */
    get(): Observable<Companies> {
        return this._httpClient.get<Companies>('/api/companies').pipe(
            tap((companies) => {
                this._companies.next(companies);
            })
        );
    }


    /**
     * Get the current logged in company data
     */
    getAll(): Observable<Companies> {
        return this._httpClient.get<Companies>('/api/companies/').pipe(
            tap((companies) => {
                this._companies.next(companies);
            })
        );
    }

    getOne(id: string): Observable<Company> {
        return this._httpClient.get<Company>(`/api/companies/${id}`).pipe(
            tap((company) => {
                this._company.next(company);
            }),
        );
    }

    getOneWithName(name: string): Observable<Company> {
        return this._httpClient.get<Company>(`/api/companies/name`, {
            params: { name }
        }).pipe(
            tap((company) => {
                this._company.next(company);
            }),
        );
    }


    getOwn(): Observable<Company> {
        return this._httpClient.get<Company>(`/api/companies/own`).pipe(
            tap((company) => {
                this._company.next(company);
            }),
        );
    }

    create(form: Company): Observable<Company> {
        return this._httpClient.post<Company>(`/api/companies/`, form).pipe(
            tap(() => {
                return this.get().subscribe();
            })
        );
    }

    update(id: string, company: Company): Observable<any> {
        return this._httpClient.patch<Company>('/api/companies/' + id, company).pipe(
            map((response) => {
                this._company.next(response);
            })
        );
    }

    remove(id: string): Observable<void> {
        return this._httpClient.delete<void>(`/api/companies/${id}`).pipe(
            tap(() => {
                return this.get().subscribe();
            })
        );
    }

    archive(id: string): Observable<Company> {
        return this._httpClient.delete<Company>(`/api/companies/${id}`).pipe(
            tap(() => {
                return this.get().subscribe();
            })
        );
    }

    restore(id: string): Observable<Company> {
        return this._httpClient.post<Company>(`/api/companies/${id}/restore`, {}).pipe(
            tap(() => {
                return this.get().subscribe();
            })
        );
    }

    sendLogo(companyId: string, file: File): Observable<any> {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.forEach((value, key) => {
            console.log(`${key}:`, value);
        });
        return this._httpClient.post<any>(
            `/api/companies/${companyId}/logo`,
            formData
        );
    }

}
